import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { isApiClientError } from "../api/errors";
import { permissionLabel } from "../entities/access/permissionLabels";
import type { Permission, Role } from "../entities/access";
import { shellBootstrapKey, shellBootstrapQuery } from "./bootstrap";
import type { ShellServices } from "./services";
import { EmptyState, LoadingPage } from "./states";

type Notice = { tone: "success" | "error"; text: string } | null;

function permissionCountLabel(count: number): string {
  const lastTwoDigits = count % 100;
  const lastDigit = count % 10;

  if (lastTwoDigits >= 11 && lastTwoDigits <= 14) return `${count} прав`;
  if (lastDigit === 1) return `${count} право`;
  if (lastDigit >= 2 && lastDigit <= 4) return `${count} права`;
  return `${count} прав`;
}

function apiMessage(error: unknown): string {
  if (isApiClientError(error)) {
    if (error.status === 403) return "Недостаточно прав для управления ролями.";
    if (error.status === 409)
      return "Данные роли уже изменились. Обновите страницу и повторите действие.";
    if (error.status === 422)
      return "Сервер отклонил изменение. Проверьте название и набор прав.";
  }
  return "Операция не выполнена. Повторите попытку.";
}

function roleSlug(name: string): string {
  const transliteration: Record<string, string> = {
    а: "a",
    б: "b",
    в: "v",
    г: "g",
    д: "d",
    е: "e",
    ё: "e",
    ж: "zh",
    з: "z",
    и: "i",
    й: "y",
    к: "k",
    л: "l",
    м: "m",
    н: "n",
    о: "o",
    п: "p",
    р: "r",
    с: "s",
    т: "t",
    у: "u",
    ф: "f",
    х: "h",
    ц: "ts",
    ч: "ch",
    ш: "sh",
    щ: "sch",
    ъ: "",
    ы: "y",
    ь: "",
    э: "e",
    ю: "yu",
    я: "ya",
  };
  const normalized = Array.from(name.toLowerCase())
    .map((character) => transliteration[character] ?? character)
    .join("")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 72);

  return `${normalized || "role"}-${Date.now().toString(36)}`;
}

function PermissionPicker({
  permissions,
  selected,
  onChange,
  contextLabel,
  disabled,
}: {
  permissions: Permission[];
  selected: readonly string[];
  onChange: (keys: string[]) => void;
  contextLabel: string;
  disabled?: boolean;
}) {
  const groups = useMemo(() => {
    const items = new Map<string, Permission[]>();
    for (const permission of permissions) {
      const group = permissionLabel(permission).group;
      items.set(group, [...(items.get(group) ?? []), permission]);
    }
    return [...items.entries()].toSorted(([left], [right]) =>
      left.localeCompare(right, "ru"),
    );
  }, [permissions]);
  const selectedSet = new Set(selected);

  return (
    <div className="role-permission-groups">
      {groups.map(([group, items]) => (
        <fieldset
          key={group}
          className="role-permission-group"
          disabled={disabled}
        >
          <legend>{group}</legend>
          {items.map((permission) => {
            const label = permissionLabel(permission);
            const checked = selectedSet.has(permission.key);
            return (
              <label key={permission.key} className="role-permission-option">
                <input
                  aria-label={`${contextLabel}: ${label.title}`}
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    onChange(
                      checked
                        ? selected.filter((key) => key !== permission.key)
                        : [...selected, permission.key],
                    )
                  }
                />
                <span>
                  <strong>{label.title}</strong>
                  <small>{label.description}</small>
                </span>
              </label>
            );
          })}
        </fieldset>
      ))}
    </div>
  );
}

function RoleEditor({
  role,
  permissions,
  editable,
  pending,
  onSave,
  onDelete,
}: {
  role: Role;
  permissions: Permission[];
  editable: boolean;
  pending: boolean;
  onSave: (input: { name: string; permissions: string[] }) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(role.name);
  const [selected, setSelected] = useState(role.permissions);

  return (
    <details className="role-editor">
      <summary
        className="role-editor__summary"
        aria-label={`Роль ${role.name}`}
      >
        <div className="role-editor__heading">
          <div>
            <h2>{role.name}</h2>
            <p>
              {editable
                ? "Изменяйте название и набор прав, затем сохраните. Роль общая: изменение увидят все организации."
                : "Системная роль защищена от изменения."}
            </p>
          </div>
          <span className="role-editor__count">
            {permissionCountLabel(selected.length)}
          </span>
        </div>
        <span className="role-editor__disclosure" aria-hidden="true" />
      </summary>
      <div className="role-editor__content">
        <label className="role-name-field">
          <span>Название роли</span>
          <input
            aria-label={`Название роли ${role.name}`}
            value={name}
            disabled={!editable || pending}
            maxLength={255}
            onChange={(event) => setName(event.target.value)}
          />
        </label>
        <PermissionPicker
          permissions={permissions}
          selected={selected}
          onChange={setSelected}
          contextLabel={role.name}
          disabled={!editable || pending}
        />
        {editable ? (
          <div className="role-editor__actions">
            <button
              className="button button--secondary"
              type="button"
              disabled={!name.trim() || pending}
              onClick={() =>
                onSave({ name: name.trim(), permissions: selected })
              }
            >
              {pending ? "Сохраняем…" : "Сохранить"}
            </button>
            <button
              className="button button--text role-editor__delete"
              type="button"
              disabled={pending}
              onClick={onDelete}
            >
              Удалить
            </button>
          </div>
        ) : null}
      </div>
    </details>
  );
}

export function RoleManagementPage({ services }: { services: ShellServices }) {
  const client = useQueryClient();
  const [name, setName] = useState("");
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [deletingRole, setDeletingRole] = useState<Role | null>(null);
  const [notice, setNotice] = useState<Notice>(null);
  const bootstrap = useQuery(shellBootstrapQuery(services));
  const roles = useQuery({
    queryKey: ["access", "roles"],
    queryFn: () => services.access!.listRoles(),
    enabled: services.access !== undefined,
  });
  const permissions = useQuery({
    queryKey: ["access", "permissions"],
    queryFn: () => services.access!.permissions(),
    enabled: services.access !== undefined,
  });
  const invalidate = async () => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ["access"] }),
      client.invalidateQueries({ queryKey: ["users"] }),
      client.invalidateQueries({ queryKey: ["user"] }),
      client.invalidateQueries({ queryKey: shellBootstrapKey }),
    ]);
  };
  const createRole = useMutation({
    mutationFn: async () => {
      let created: Role | undefined;

      try {
        created = await services.access!.createRole({
          slug: roleSlug(name.trim()),
          name: name.trim(),
        });
        await services.access!.replaceRolePermissions(
          created.id,
          selectedPermissions,
        );
      } catch (error) {
        // Создание роли и сохранение её прав — разные операции API. Если вторая
        // операция не удалась, не оставляем в списке роль без выбранных прав.
        if (created) {
          await services.access!.deleteRole(created.id).catch(() => undefined);
        }
        throw error;
      }
    },
    onSuccess: async () => {
      setName("");
      setSelectedPermissions([]);
      setNotice({ tone: "success", text: "Роль создана и права сохранены." });
      await invalidate();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const updateRole = useMutation({
    mutationFn: async ({
      role,
      input,
    }: {
      role: Role;
      input: { name: string; permissions: string[] };
    }) => {
      await services.access!.updateRole(role.id, {
        slug: role.slug,
        name: input.name,
      });
      await services.access!.replaceRolePermissions(role.id, input.permissions);
    },
    onSuccess: async () => {
      setNotice({ tone: "success", text: "Роль и её права сохранены." });
      await invalidate();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });
  const deleteRole = useMutation({
    mutationFn: (role: Role) => services.access!.deleteRole(role.id),
    onSuccess: async () => {
      setDeletingRole(null);
      setNotice({ tone: "success", text: "Роль удалена." });
      await invalidate();
    },
    onError: (error) => setNotice({ tone: "error", text: apiMessage(error) }),
  });

  if (services.access === undefined) {
    return (
      <EmptyState
        title="Раздел недоступен"
        description="Сервис управления ролями не подключён."
      />
    );
  }
  if (roles.isPending || permissions.isPending || bootstrap.isPending) {
    return <LoadingPage label="Загружаем роли и каталог прав" />;
  }
  if (roles.isError || permissions.isError || bootstrap.isError) {
    return (
      <EmptyState
        title="Не удалось загрузить роли"
        description={apiMessage(
          roles.error ?? permissions.error ?? bootstrap.error,
        )}
      />
    );
  }

  return (
    <section
      className="workspace-page roles-page"
      aria-labelledby="roles-title"
    >
      <div className="page-heading">
        <p className="eyebrow">Администрирование</p>
        <h1 id="roles-title">Создание ролей</h1>
        <p>
          Роль — это набор прав с названием, общий для всех организаций. Где
          роль будет действовать, решается при назначении сотруднику.
        </p>
      </div>
      {notice ? (
        <p
          className={`operation-notice operation-notice--${notice.tone}`}
          role="status"
        >
          {notice.text}
        </p>
      ) : null}
      <details className="role-creator" aria-labelledby="role-creator-title">
        <summary
          className="role-editor__summary"
          aria-label="Создать новую роль"
        >
          <div className="role-editor__heading">
            <div>
              <h2 id="role-creator-title">Новая роль</h2>
              <p>
                Название увидят администраторы при назначении роли пользователю.
              </p>
            </div>
            <span className="role-editor__count">
              {permissionCountLabel(selectedPermissions.length)}
            </span>
          </div>
          <span className="role-editor__disclosure" aria-hidden="true" />
        </summary>
        <div className="role-editor__content">
          <label className="role-name-field">
            <span>Название роли</span>
            <input
              aria-label="Название новой роли"
              value={name}
              disabled={createRole.isPending}
              maxLength={255}
              placeholder="Например, Специалист по документам"
              onChange={(event) => setName(event.target.value)}
            />
          </label>
          <PermissionPicker
            permissions={permissions.data ?? []}
            selected={selectedPermissions}
            onChange={setSelectedPermissions}
            contextLabel="Новая роль"
            disabled={createRole.isPending}
          />
          <button
            className="button button--primary"
            type="button"
            disabled={!name.trim() || createRole.isPending}
            onClick={() => createRole.mutate()}
          >
            {createRole.isPending ? "Создаём…" : "Создать роль"}
          </button>
        </div>
      </details>
      <section className="roles-list" aria-labelledby="roles-list-title">
        <div className="panel-heading">
          <div>
            <h2 id="roles-list-title">Существующие роли</h2>
            <p>
              Каталог общий для всех организаций: изменение роли увидят все,
              кому она выдана. Системные роли защищены от изменения.
            </p>
          </div>
        </div>
        {roles.data?.length ? (
          roles.data.map((role) => (
            <RoleEditor
              key={role.id}
              role={role}
              permissions={permissions.data ?? []}
              editable={!role.is_system}
              pending={updateRole.isPending || deleteRole.isPending}
              onSave={(input) => updateRole.mutate({ role, input })}
              onDelete={() => setDeletingRole(role)}
            />
          ))
        ) : (
          <EmptyState
            title="Ролей пока нет"
            description="Создайте первую роль — набор прав, который потом выдадите сотрудникам."
          />
        )}
      </section>
      {deletingRole ? (
        <div
          className="modal"
          role="dialog"
          aria-modal="true"
          aria-label="Подтверждение удаления роли"
        >
          <button
            className="modal-backdrop"
            type="button"
            aria-label="Нет"
            onClick={() => setDeletingRole(null)}
          />
          <div className="modal-card">
            <h2>Вы уверены?</h2>
            <p>
              После удаления роль «{deletingRole.name}» и её назначения будут
              удалены.
            </p>
            <div className="modal-actions">
              <button
                className="button button--secondary"
                type="button"
                onClick={() => setDeletingRole(null)}
              >
                Нет
              </button>
              <button
                className="button button--primary"
                type="button"
                disabled={deleteRole.isPending}
                onClick={() => deleteRole.mutate(deletingRole)}
              >
                Да
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
}
